// Connexion par code à usage unique reçu par e-mail.
import type { JSX } from 'preact'
import { useState } from 'preact/hooks'
import { createClient } from '@feedbaacks/sdk'
import { Button, errorMessage, Spinner } from '@feedbaacks/ui'
import { d } from '../i18n'
import { writeToken } from '../session'

export function Login({ apiUrl, onSignedIn }: { apiUrl: string; onSignedIn: (token: string) => void }): JSX.Element {
  // La connexion n'a pas encore de jeton : les deux appels sont publics.
  const [client] = useState(() => createClient({ baseUrl: apiUrl, auth: { type: 'token', token: '' } }))
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(errorMessage(err, d('error_generic')))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div class="fb-root dash-login">
      <form class="dash-card" data-testid="dash-login"
        onSubmit={(e) => {
          e.preventDefault()
          // Valeurs lues dans le formulaire à l'envoi : un remplissage
          // automatique (gestionnaire de mots de passe) n'émet pas toujours
          // d'événement input.
          const form = new FormData(e.currentTarget)
          const typedEmail = String(form.get('email') ?? email).trim()
          void run(async () => {
            if (step === 'email') {
              setEmail(typedEmail)
              await client.account.requestCode(typedEmail)
              setStep('code')
            } else {
              const r = await client.account.verifyCode(email.trim(), String(form.get('code') ?? code).trim())
              writeToken(r.token)
              onSignedIn(r.token)
            }
          })
        }}>
        <h1>{d('login_title')}</h1>
        {step === 'email' ? (
          <label class="dash-field">
            <span>{d('login_email')}</span>
            <input class="fb-input" name="email" type="email" required autoComplete="email" value={email} onInput={(e) => setEmail(e.currentTarget.value)} data-testid="dash-login-email" />
          </label>
        ) : (
          <>
            <p class="fb-muted">{d('login_sent')}</p>
            <label class="dash-field">
              <span>{d('login_code')}</span>
              <input class="fb-input dash-code" name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code}
                onInput={(e) => setCode(e.currentTarget.value)} data-testid="dash-login-code" />
            </label>
          </>
        )}
        {error && <p role="alert" class="fb-inline-error">{error}</p>}
        <Button type="submit" variant="primary" disabled={busy} testId="dash-login-submit">
          {busy && <Spinner size={14} />}
          {step === 'email' ? d('login_send') : d('login_verify')}
        </Button>
        {step === 'code' && (
          <button type="button" class="fb-link" onClick={() => setStep('email')}>{d('login_other_email')}</button>
        )}
      </form>
    </div>
  )
}
